import React, {useEffect, useState, useMemo} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';

import {useQuery} from '@apollo/client/react';
import {Crown, Plus} from 'lucide-react';
import {StatusBadge} from '../components/StatusBadge';
import {FeatureBadge} from '../components/FeatureBadge';
import {Avatar} from '../components/Avatar';
import {StatsCard} from '../components/StatsCard';
import {GraphQLChangeRequestButton} from '../components/GraphQLChangeRequestButton/GraphQLChangeRequestButton';
import {CreateOrganisationModal} from '../components/CreateOrganisationModal';
import {gql} from 'types/__generated__';

const ORGANISATIONS_QUERY = gql(`
  query Organisations {
    organisations {
      id
      name
      description
      active
      numberOfUsers
      numberOfArtworks
      accountOwner {
        id
        username
        firstName
        lastName
      }
    }
  }
`);

const PAGE_SIZE = 20;

export default function OrganisationsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const searchTerm = searchParams.get('q') || '';
  const filterActive = (searchParams.get('filter') as 'all' | 'active' | 'inactive') || 'active';
  const currentPage = parseInt(searchParams.get('page') || '0', 10) || 0;

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const {loading, error, data} = useQuery(ORGANISATIONS_QUERY, {
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  });

  // The schema types this as [Organisation] — nullable elements — so drop the holes
  // once here rather than asserting non-null at each use below.
  const organisations = (data?.organisations ?? []).filter((org) => org !== null);

  const filteredOrganisations = useMemo(
    () =>
      organisations.filter((org) => {
        const matchesSearch =
          org.name?.toLowerCase().includes(searchTerm.toLowerCase()) || org.description?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesFilter = filterActive === 'all' || (filterActive === 'active' && org.active) || (filterActive === 'inactive' && !org.active);

        return matchesSearch && matchesFilter;
      }),
    [organisations, searchTerm, filterActive],
  );

  const totalPages = Math.ceil(filteredOrganisations.length / PAGE_SIZE);
  const paginatedOrganisations = useMemo(
    () => filteredOrganisations.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE),
    [filteredOrganisations, currentPage],
  );

  const updateParams = (updates: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value === null || value === '') {
          next.delete(key);
        } else {
          next.set(key, value);
        }
      }
      return next;
    });
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <h3 className="text-red-800 dark:text-red-200 font-semibold">Error loading organisations</h3>
          <p className="text-red-600 dark:text-red-300 mt-1">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Organisations</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Manage organisations and subscriptions</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            title="Create a new organisation"
          >
            <Plus size={18} />
            <span className="font-medium">New Organisation</span>
          </button>
          <GraphQLChangeRequestButton context="organisations-list" />
        </div>
      </div>

      <CreateOrganisationModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={(organisationId) => navigate(`/admin/organisations/${organisationId}`)}
      />

      {/* Filters and Search */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search organisations by name or description..."
            value={searchTerm}
            onChange={(e) => updateParams({q: e.target.value || null, page: null})}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => updateParams({filter: 'all', page: null})}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filterActive === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            All ({organisations.length})
          </button>
          <button
            onClick={() => updateParams({filter: null, page: null})}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filterActive === 'active'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            Active ({organisations.filter((o) => o.active).length})
          </button>
          <button
            onClick={() => updateParams({filter: 'inactive', page: null})}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filterActive === 'inactive'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            Inactive ({organisations.filter((o) => !o.active).length})
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <StatsCard label="Total Organisations" value={organisations.length} />
        <StatsCard label="Total Users" value={organisations.reduce((sum, org) => sum + (org.numberOfUsers || 0), 0)} valueColor="text-blue-600" />
      </div>

      {/* Organisations Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fadeIn">
        {paginatedOrganisations.map((org) => (
          <div
            key={org.id}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-shadow"
          >
            {/* Card Header */}
            <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Avatar name={org.name} size="md" shape="rounded" />
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">{org.name}</h3>
                    {org.description && <p className="text-sm text-gray-600 dark:text-gray-400">{org.description}</p>}
                  </div>
                </div>
                <StatusBadge type="organisation" active={org.active} />
              </div>
            </div>

            {/* Card Body */}
            <div className="px-6 py-4">
              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded">
                    <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Users</p>
                    <p className="text-sm font-bold text-gray-900 dark:text-gray-100">{org.numberOfUsers || 0}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded">
                    <svg className="w-4 h-4 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Artworks</p>
                    <p className="text-sm font-bold text-gray-900 dark:text-gray-100">{org.numberOfArtworks || 0}</p>
                  </div>
                </div>
              </div>

              <p className="mt-4 text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                <Crown size={14} className="text-amber-500" />
                <span className="font-medium">Account owner:</span>
                {org.accountOwner ? (
                  <button
                    onClick={() => navigate(`/admin/users/${org.accountOwner!.username}`)}
                    className="text-blue-600 dark:text-blue-400 hover:underline truncate"
                    title={org.accountOwner.username}
                  >
                    {`${org.accountOwner.firstName} ${org.accountOwner.lastName}`.trim() || org.accountOwner.username}
                  </button>
                ) : (
                  <span className="italic">none</span>
                )}
              </p>
            </div>

            {/* Card Footer */}
            <button
              onClick={() => navigate(`/admin/organisations/${org.id}`)}
              className="w-full px-6 py-3 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors cursor-pointer"
              title="View organisation details"
            >
              <p className="text-xs font-mono italic text-gray-500 dark:text-gray-400">{org.id}</p>
              <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        ))}
      </div>

      {paginatedOrganisations.length === 0 && !loading && (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <p className="text-gray-500 dark:text-gray-400">No organisations found matching your criteria</p>
        </div>
      )}

      {/* Results count and Pagination */}
      <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Showing {paginatedOrganisations.length} of {filteredOrganisations.length} organisations
          {totalPages > 1 && ` - Page ${currentPage + 1} of ${totalPages}`}
        </div>

        {totalPages > 1 && (
          <div className="flex gap-2">
            <button
              onClick={() => updateParams({page: currentPage - 1 === 0 ? null : String(currentPage - 1)})}
              disabled={currentPage === 0}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <div className="flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400">
              Page {currentPage + 1} of {totalPages}
            </div>
            <button
              onClick={() => updateParams({page: String(currentPage + 1)})}
              disabled={currentPage >= totalPages - 1}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
